import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import usePortal from 'react-useportal';

import { useDispatch, useSelector } from 'react-redux';
import { sidebarOpenRightPanel } from '../../../../redux/actions/Sidebar';
import { transferShowUnshowFilter } from '../../../../redux/actions/Transfers';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import {
  isFilterShown,
} from '../../../../redux/selector/Transfers';
import { isPremiumUser } from '../../../../redux/selector/User';
import { POPUPS } from '../../../../service/constants';

import { ReactComponent as Icon } from '../../../../assets/img/down-icon.svg';
import { ReactComponent as Crown } from '../../../../assets/img/crown.svg';
import { ReactComponent as SlidersIcon } from '../../../../assets/img/header/sliders.svg';
import {
  StyledManipulatorContainer,
  StyledFilterContainer,
  StyledOpenFilterContainer,
  StyledOpenFilterButton,
  StyledFilterTabs,
  StyledFilterTab,
  StyledFilterField,
  StyledFilterActions,
  StyledClearButton,
  StyledSheetScrim,
  StyledSheet,
  StyledSheetHead,
  StyledFab,
  StyledFabBadge,
  StyledSortList,
  StyledSortOption,
} from './styled';
import CSVReport from '../CSVReport';

export default function SearchTableFilter({
  allColumns = [],
  hideCsv = false,
  filters = [],
  inline = false,
  sortOptions = [],
  sortValue = null,
  onSortChange,
}) {
  const dispatch = useDispatch();
  const [filterTab, setFilterTab] = useState('basic');
  const [sheetTab, setSheetTab] = useState('filter');
  const [sheetMounted, setSheetMounted] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isFilterVisible = useSelector(isFilterShown);
  const isPremium = useSelector(isPremiumUser);
  const { Portal } = usePortal({
    bindTo:
      document && document.querySelector('.right-sidebar .filter-container'),
  });
  const hasSort = inline && Array.isArray(sortOptions) && sortOptions.length > 0;

  const basicColumns = allColumns.filter(
    (column) => column.canFilter && column.filterGroup !== 'advanced'
  );
  const advancedColumns = allColumns.filter(
    (column) => column.canFilter && column.filterGroup === 'advanced'
  );
  const visibleColumns =
    filterTab === 'advanced' ? advancedColumns : basicColumns;
  const filterSignature = (filters || [])
    .map((item) => `${item.id}:${JSON.stringify(item.value)}`)
    .join('|');

  const openAdvancedTab = () => {
    if (!isPremium) {
      dispatch(
        showPopupWindow(
          POPUPS.MOBILE_REQUIRED_POPUP,
          POPUPS.MOBILE_REQUIRED_POPUP
        )
      );
      return;
    }
    setFilterTab('advanced');
  };

  const clearCurrentFilters = () => {
    visibleColumns.forEach((column) => column.setFilter(undefined));
  };

  const toggleFilter = () => {
    if (!inline) {
      dispatch(sidebarOpenRightPanel(true));
    }
    dispatch(transferShowUnshowFilter(!isFilterVisible));
  };

  useEffect(() => {
    if (!inline) return undefined;
    if (isFilterVisible) {
      setSheetMounted(true);
      const frame = window.requestAnimationFrame(() => setSheetOpen(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setSheetOpen(false);
    const timer = window.setTimeout(() => setSheetMounted(false), 320);
    return () => window.clearTimeout(timer);
  }, [inline, isFilterVisible]);

  useEffect(() => {
    if (!inline || !isFilterVisible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [inline, isFilterVisible]);

  const activeCount = (filters || []).length;
  const panel = (
    <StyledManipulatorContainer
      openFilter={isFilterVisible.toString()}
      data-filter-state={filterSignature}
      $inline={inline}
      $sheet={inline}
    >
      <StyledFilterTabs>
        <StyledFilterTab
          type="button"
          className={filterTab === 'basic' ? 'active' : ''}
          onClick={() => setFilterTab('basic')}
        >
          Basic
        </StyledFilterTab>
        <StyledFilterTab
          type="button"
          className={filterTab === 'advanced' ? 'active' : ''}
          onClick={openAdvancedTab}
        >
          Advanced
          <Crown />
        </StyledFilterTab>
      </StyledFilterTabs>
      {visibleColumns.map((column) => {
        const filterElement = column.render('Filter');
        return (
          <StyledFilterField key={column.id}>
            {React.isValidElement(filterElement)
              ? React.cloneElement(filterElement, {
                  column: {
                    ...column,
                    id: column.id,
                    Header: column.Header,
                    filterValue: column.filterValue,
                    setFilter: column.setFilter,
                    preFilteredRows: column.preFilteredRows,
                    filterOptions: column.filterOptions,
                  },
                })
              : filterElement}
          </StyledFilterField>
        );
      })}
      <StyledFilterActions>
        <StyledClearButton type="button" onClick={clearCurrentFilters}>
          Clear
        </StyledClearButton>
      </StyledFilterActions>
    </StyledManipulatorContainer>
  );

  const button = (
    <StyledOpenFilterContainer
      className="filter-section"
      $inline={false}
      onClick={toggleFilter}
    >
      <span>{activeCount ? `Filter (${activeCount})` : 'Filter'}</span>
      <StyledOpenFilterButton>
        <Icon
          style={{
            transform: isFilterVisible ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </StyledOpenFilterButton>
    </StyledOpenFilterContainer>
  );

  const sortPanel = hasSort ? (
    <StyledSortList>
      {sortOptions.map((option) => (
        <StyledSortOption
          key={option.value}
          type="button"
          $active={sortValue && sortValue.value === option.value}
          onClick={() => onSortChange && onSortChange(option)}
        >
          {option.label}
        </StyledSortOption>
      ))}
    </StyledSortList>
  ) : null;

  const sheet =
    inline && sheetMounted && typeof document !== 'undefined'
      ? createPortal(
          <>
            <StyledSheetScrim
              type="button"
              $open={sheetOpen}
              aria-label="Close filter"
              onClick={toggleFilter}
            />
            <StyledSheet role="dialog" aria-label="Filter and sort" $open={sheetOpen}>
              <StyledSheetHead>
                <h2>Filter & sort</h2>
                <StyledClearButton type="button" onClick={toggleFilter}>
                  Done
                </StyledClearButton>
              </StyledSheetHead>
              {hasSort ? (
                <StyledFilterTabs>
                  <StyledFilterTab
                    type="button"
                    className={sheetTab === 'filter' ? 'active' : ''}
                    onClick={() => setSheetTab('filter')}
                  >
                    Filter
                    {activeCount ? ` · ${activeCount}` : ''}
                  </StyledFilterTab>
                  <StyledFilterTab
                    type="button"
                    className={sheetTab === 'sort' ? 'active' : ''}
                    onClick={() => setSheetTab('sort')}
                  >
                    Sort
                  </StyledFilterTab>
                </StyledFilterTabs>
              ) : null}
              {sheetTab === 'sort' && hasSort ? sortPanel : panel}
            </StyledSheet>
          </>,
          document.body
        )
      : null;

  return (
    <>
      {!hideCsv && (
        <StyledFilterContainer>
          <CSVReport isPremium={isPremium} />
        </StyledFilterContainer>
      )}
      {inline ? (
        <>
          <StyledFab
            type="button"
            aria-label="Filter and sort"
            onClick={toggleFilter}
          >
            <SlidersIcon />
            {activeCount ? <StyledFabBadge>{activeCount}</StyledFabBadge> : null}
          </StyledFab>
          {sheet}
        </>
      ) : (
        <>
          {button}
          <Portal>{panel}</Portal>
        </>
      )}
    </>
  );
}
